import React, { useEffect, useState } from "react";
import { Loader2, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { ago } from "@/lib/sk";
import { RatesBar } from "@/components/canvas/RatesBar";

const KIND = { proposal: { label: "KI-forslag", cls: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]" }, undo: { label: "Angret", cls: "bg-black/[0.06] text-foreground/70" }, action: { label: "Handling", cls: "bg-black/[0.05] text-foreground/70" }, edit: { label: "Redigert", cls: "bg-black/[0.05] text-foreground/70" } };

function Section({ title, right, children, testId }) {
  return (
    <section className="mt-4 first:mt-0" data-testid={testId}>
      <div className="flex items-center justify-between text-[11px] font-semibold text-muted-foreground"><span>{title}</span>{right}</div>
      <div className="mt-1.5">{children}</div>
    </section>
  );
}

/** Rate version, how the numbers are computed and the traceable change log – under «Beregning og historikk», never on the work surface. */
export function BasisPanel({ ctx, project, ops, busy }) {
  const [changes, setChanges] = useState(null);
  const [err, setErr] = useState(null);
  const [rules, setRules] = useState(false);
  const undoId = ctx.undo?.id;
  useEffect(() => {
    let alive = true;
    api.get(`/projects/${project.id}/changes`, { params: { limit: 40 } }).then((r) => alive && setChanges(r.data.changes || [])).catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
  }, [project.id, undoId]);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 pt-1 scrollbar-thin" data-testid="basis-panel">
      <Section title="Satser og prisliste" testId="basis-rates">
        <RatesBar ctx={ctx} project={project} ops={ops} compact />
      </Section>
      <Section title="Slik regnes det" right={<button type="button" onClick={() => setRules((v) => !v)} className="text-[12px] text-muted-foreground hover:text-foreground" data-testid="basis-rules-toggle">{rules ? "Skjul" : "Vis"}</button>} testId="basis-rules">
        {rules ? <p className="text-[13px] leading-relaxed text-muted-foreground" data-testid="kalkyle-lead">Kostelementer per arbeidspost: arbeid, materiell, utstyr og underleverandør. Summer, DB og påslag regnes av systemet – aldri av KI. Opsjoner, enhetspriser uten mengde og poster uten pris holdes utenfor hovedsummen. DB % = (pris − kost) / pris. Påslag % = (pris − kost) / kost. Prisliste-elementer med ferdig salgspris får ikke nytt påslag. Tom mengde eller tom pris betyr «ikke bestemt» – aldri 0.</p> : null}
      </Section>
      <Section title="Endringslogg" right={changes ? <span className="font-mono">{changes.length}</span> : null} testId="basis-changes">
        {err ? <p className="text-[12px] text-[hsl(var(--v-blocked))]">{err}</p> : !changes ? <div className="flex h-16 items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /></div> : !changes.length ? <p className="text-[12px] text-muted-foreground" data-testid="basis-changes-empty">Ingen endringer på saken ennå.</p> : (
          <ul className="divide-y divide-black/[0.05] rounded-[10px] border border-black/[0.06]" data-testid="basis-changes-list">
            {changes.map((c) => {
              const k = KIND[c.kind] || KIND.edit;
              const canUndo = ctx.undo && ctx.undo.id === c.id;
              return (
                <li key={c.id} className={cn("px-2.5 py-1.5 text-[12px] leading-snug", c.undone && "opacity-55")} data-testid={`basis-change-${c.id}`} data-undone={c.undone || undefined}>
                  <div className="flex items-start gap-1.5">
                    <span className={cn("v-badge mt-[1px] !h-4 shrink-0 !px-1.5 !text-[9.5px]", k.cls)}>{k.label}</span>
                    <span className={cn("min-w-0 flex-1", c.undone && "line-through")}>{c.label}</span>
                    {canUndo ? <button type="button" onClick={() => ops.undo(c.id).catch(() => {})} disabled={busy} className="inline-flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-medium text-foreground/70 hover:bg-black/[0.05] hover:text-foreground disabled:opacity-50" data-testid={`basis-undo-${c.id}`}><Undo2 className="h-3 w-3" /> Angre</button> : null}
                  </div>
                  <div className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">rev {c.revision} · {c.by?.name || "system"} · {ago(c.at)}{c.undone ? " · angret" : ""}</div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
